import { BehaviorSubject, Observable } from 'rxjs';
import { InMemoryStorage } from './in-memory.storage';

export enum StorageType {
  SESSION,
  LOCAL,
  MEMORY,
}

/**
 * Stores an item against a key using the storage mechanism requested. Tells observers when the item is updated (put and cleared).
 */
export class StorageService<T> {
  private readonly storage: Storage;
  private readonly key: string;
  private itemSubject: BehaviorSubject<Readonly<T> | null>;
  private itemChange$: Observable<Readonly<T> | null>;

  constructor(key: string, type?: StorageType) {
    this.key = key;
    this.storage = this.initStorage(key, type);
    const initialValue = this.get();
    this.itemSubject = new BehaviorSubject<Readonly<T> | null>(initialValue);
    this.itemChange$ = this.itemSubject.asObservable();
  }

  get(): T | null {
    const json = this.storage.getItem(this.key);
    return json ? <T>JSON.parse(json) : null;
  }

  put(item: T): void {
    this.storage.setItem(this.key, JSON.stringify(item));
    this.itemSubject.next(item);
  }

  clear(): void {
    this.storage.clear();
    this.itemSubject.next(null);
  }

  observe(): Observable<T> {
    return this.itemChange$ as Observable<T>;
  }

  private initStorage(key: string, type?: StorageType): Storage {
    if (type === StorageType.LOCAL) {
      return window.localStorage;
    }

    if (type === StorageType.SESSION) {
      return window.sessionStorage;
    }

    // default store in memory
    return new InMemoryStorage(key);
  }
}
